import { registerNewUser } from '@/api/register-new-user.ts'
import { Button } from '@/components/ui/button.tsx'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form.tsx'
import { Input } from '@/components/ui/input.tsx'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

export const Route = createFileRoute('/auth/sign-up')({
  component: SignUp,
  head: () => ({
    meta: [
      {
        title: 'Sign on | Okami',
      },
    ],
  }),
})

const formSchema = z
  .object({
    name: z.string().min(4, 'Informe um nome com mais de 4 caracteres'),
    email: z.string().email('Informe um email válido'),
    password: z.string().min(8, 'Informe uma senha com mais de 8 caracteres'),
    confirmPassword: z.string().min(8, 'Informe uma senha com mais de 8 caracteres'),
  })
  .superRefine((context, ctx) => {
    const matchPassword = context.password === context.confirmPassword

    if (!matchPassword) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'As senhas não são coincidentes',
        path: ['confirmPassword'],
      })
    }
  })

type FormType = z.infer<typeof formSchema>

function SignUp() {
  const navigate = Route.useNavigate()

  const [canShowPassword, setCanShowPassword] = useState(false)

  const form = useForm<FormType>({
    values: {
      confirmPassword: '',
      email: '',
      name: '',
      password: '',
    },
    resolver: zodResolver(formSchema),
  })

  const { mutateAsync: registerUserCall } = useMutation({
    mutationKey: ['registerUser'],
    mutationFn: registerNewUser,
    meta: {
      successMessage: 'Usuário registrado com sucesso',
      errorMessage: 'Erro ao registrar usuário',
    },
    onSuccess() {
      form.reset()
      toast.success('Usuário registrado com sucesso')
      navigate({ to: '/' })
    },
    onError(error) {
      if (error.isUserAlreadyExistsError()) {
        toast.error('E-mail já cadastrado')
        form.reset()
        return
      }

      toast.error('Erro ao registrar usuário')
    },
  })

  async function handleSigOut(values: FormType) {
    await registerUserCall(values)
  }

  function handleTogglePasswordShow() {
    setCanShowPassword((prev) => !prev)
  }

  const inputPasswordType = canShowPassword ? 'text' : 'password'

  return (
    <div className="p-8">
      <div className="flex w-[320px] flex-col justify-center gap-6">
        <header className="flex flex-col gap-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Cadastro</h1>
          <p className="text-sm text-muted-foreground">
            Cadastre-se para acompanhar suas obras favoritas e muito mais...
          </p>
        </header>

        <Form {...form}>
          <form className="flex flex-col gap-4 " onSubmit={form.handleSubmit(handleSigOut)}>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome</FormLabel>
                  <FormControl>
                    <Input placeholder="Jhon Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>E-mail</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="okami@email.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="relative">
                  <FormLabel>Senha</FormLabel>
                  <FormControl>
                    <Input type={inputPasswordType} placeholder="******" {...field} />
                  </FormControl>

                  {canShowPassword ? (
                    <EyeOff onClick={handleTogglePasswordShow} className="absolute right-2 top-8" />
                  ) : (
                    <Eye onClick={handleTogglePasswordShow} className="absolute right-2 top-8" />
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirmar senha</FormLabel>
                  <FormControl>
                    <Input type={inputPasswordType} placeholder="******" {...field} />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />
            <Button disabled={form.formState.isSubmitting} className="w-full" type="submit">
              {form.formState.isSubmitting ? <Loader2 className="mr-2 size-4 animate-spin" /> : 'Registrar'}
            </Button>

            <Button asChild type="button" variant="link">
              <Link to="/auth/sign-in">Já possui uma conta ?</Link>
            </Button>
          </form>
        </Form>
      </div>
    </div>
  )
}
